import { useState, useMemo } from "react";
import { Button, Dialog, Select, TextField } from "@jkast90/kast-common/ui";
import styles from "./ClaimDialog.module.css";

const FORM_ID = "claim-form";

interface CalendarEvent {
  id: number;
  title: string;
  date: string;
  type: string;
}

interface ClaimDialogProps {
  open: boolean;
  onClose: () => void;
  onClaim: (revealDate: string) => void;
  itemDescription: string;
  wisheeName: string;
  events?: CalendarEvent[];
}

const SESSION_STORAGE_KEY = "claimDialog_selectedEventId";

export default function ClaimDialog({
  open,
  onClose,
  onClaim,
  itemDescription,
  wisheeName,
  events = [],
}: ClaimDialogProps) {
  const [revealDate, setRevealDate] = useState("");
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);

  // Get tomorrow's date as the minimum date
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split("T")[0];

  // Filter events to only show future events
  const futureEvents = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return events
      .filter((event) => {
        const eventDate = new Date(event.date);
        eventDate.setHours(0, 0, 0, 0);
        return eventDate >= today;
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [events]);

  // On dialog open (or when the events change while open), restore the last
  // selected event from session storage. Adjusted during render, not in an
  // effect, so the dialog never paints without it.
  const restoreFor = open ? futureEvents : null;
  const [restoredFor, setRestoredFor] = useState<CalendarEvent[] | null>(null);
  if (restoreFor !== restoredFor) {
    setRestoredFor(restoreFor);
    if (restoreFor && restoreFor.length > 0) {
      const storedEventId = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (storedEventId) {
        const eventId = parseInt(storedEventId, 10);
        const event = restoreFor.find((e) => e.id === eventId);
        if (event) {
          setSelectedEventId(eventId);
          setRevealDate(event.date);
        }
      }
    }
  }

  // Handle event selection change
  const handleEventChange = (eventId: number | null) => {
    setSelectedEventId(eventId);
    if (eventId) {
      const event = futureEvents.find((e) => e.id === eventId);
      if (event) {
        setRevealDate(event.date);
        sessionStorage.setItem(SESSION_STORAGE_KEY, eventId.toString());
      }
    } else {
      // Clear session storage if "Custom date" is selected
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
  };

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!revealDate) return; // the date field is required: the browser asks for it
    onClaim(revealDate);
    setRevealDate("");
    onClose();
  };

  const handleCancel = () => {
    setRevealDate("");
    setSelectedEventId(null);
    onClose();
  };

  // Format date for display
  const formatEventDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      title="Claim Gift"
      footer={
        <>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button variant="primary" type="submit" form={FORM_ID}>
            Claim Gift
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={handleSubmit}>
        <p className={styles.description}>
          You're claiming: <strong>{itemDescription}</strong> for{" "}
          <strong>{wisheeName}</strong>
        </p>

        {futureEvents.length > 0 && (
          <Select
            label="Choose an event for this gift:"
            placeholder="-- Select an event or use custom date --"
            options={futureEvents.map((event) => ({
              value: String(event.id),
              label: `${event.title} (${formatEventDate(event.date)})`,
            }))}
            value={selectedEventId === null ? null : String(selectedEventId)}
            onChange={(value) =>
              handleEventChange(value ? parseInt(value, 10) : null)
            }
          />
        )}

        <TextField
          label={
            futureEvents.length > 0
              ? `Reveal date${selectedEventId ? " (from selected event)" : ""}:`
              : `When can ${wisheeName} see this gift and send a thank you?`
          }
          type="date"
          value={revealDate}
          onChange={(e) => {
            setRevealDate(e.target.value);
            // If user manually changes date, clear the event selection
            if (selectedEventId) {
              const event = futureEvents.find(
                (ev) => ev.id === selectedEventId,
              );
              if (event && e.target.value !== event.date) {
                setSelectedEventId(null);
                sessionStorage.removeItem(SESSION_STORAGE_KEY);
              }
            }
          }}
          min={minDate}
          required
          hint={`This is when ${wisheeName} can see this gift and send a thank you.`}
        />
      </form>
    </Dialog>
  );
}
